import { ErrorMessage, PageContainer, Spinner } from "@/components";
import {
  useBookmarksQuery,
  useRemoveBookmarkMutation,
} from "@/hooks/api/bookmark.queries";
import { BookmarkType } from "@/services/bookmark.service";
import {
  Bookmark,
  BookmarkX,
  Briefcase,
  Building2,
  ChevronRight,
  IndianRupee,
  Loader2,
  RefreshCw,
  Sparkles,
  Wifi,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export default function BookmarksPage() {
  const navigate = useNavigate();
  const { data: bookmarks = [], isLoading, isError, refetch } = useBookmarksQuery();
  const removeMutation = useRemoveBookmarkMutation();

  if (isError) {
    return <ErrorMessage message="Failed to load bookmarks." onRetry={() => refetch()} />;
  }

  return (
    <PageContainer className="min-h-screen">
      {/* Hero Header */}
      <div className="relative mb-10 overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-indigo-700 via-blue-800 to-indigo-950 border border-blue-500/20 p-10 text-white shadow-2xl shadow-blue-900/20">
        {/* Animated Radial Background */}
        <div className="absolute top-[-50%] left-[-20%] w-[140%] h-[200%] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-400/20 via-blue-400/5 to-transparent blur-3xl rounded-full animate-[spin_40s_linear_infinite]" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[80%] h-[120%] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-400/15 via-indigo-400/5 to-transparent blur-3xl rounded-full" />
        
        <div className="relative z-10 flex flex-col items-start gap-1">
          <div className="flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-cyan-400/20 border border-cyan-400/30 backdrop-blur-md">
            <Sparkles size={16} className="text-cyan-300" />
            <span className="text-[10px] font-black text-cyan-100 uppercase tracking-widest">
              Saved Collection
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-50 to-cyan-200">
            My Bookmarks
          </h1>
          <p className="text-blue-100/90 text-lg max-w-xl font-medium leading-relaxed">
            Your personal vault of high-potential roles. Review and apply when you're ready to make your move.
          </p>
          <div className="mt-8 flex items-center gap-4 text-sm font-bold text-blue-50">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl border border-white/20">
              <Bookmark size={16} className="fill-cyan-400 text-cyan-400" />
              <span className="text-white">{isLoading ? "..." : bookmarks.length}</span> Roles Saved
            </div>
            <button
              onClick={() => refetch()}
              className="flex items-center gap-2 hover:text-white transition-colors hover:bg-white/10 px-4 py-2 rounded-xl border border-transparent hover:border-white/20 group"
            >
              <RefreshCw size={14} className={cn("text-cyan-400 group-hover:text-cyan-300", isLoading && "animate-spin")} /> 
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Spinner />
          <p className="text-gray-400 text-sm animate-pulse">Loading your saved roles...</p>
        </div>
      ) : bookmarks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 bg-white rounded-2xl border border-dashed border-gray-200 text-center">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-5">
            <Bookmark size={32} className="text-blue-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No bookmarks yet</h3>
          <p className="text-gray-400 text-sm max-w-xs mb-6">
            Save internships while browsing so you can come back and apply later.
          </p>
          <button
            onClick={() => navigate("/discover/internships")}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition flex items-center gap-2"
          >
            <Briefcase size={16} /> Browse Internships
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {bookmarks.map((bm: BookmarkType) => (
            <BookmarkCard
              key={bm.id}
              bookmark={bm}
              onView={() => navigate(`/discover/internships/${bm.internship_id}`)}
              onRemove={() => removeMutation.mutate(bm.id)}
              isRemoving={removeMutation.isPending && removeMutation.variables === bm.id}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

// ─── Bookmark Card ─────────────────────────────────────────────────────────────
function BookmarkCard({
  bookmark,
  onView,
  onRemove,
  isRemoving,
}: {
  bookmark: BookmarkType;
  onView: () => void;
  onRemove: () => void;
  isRemoving: boolean;
}) {
  const title = bookmark.internship_title || "Untitled Role";
  const company = bookmark.company_name || "Confidential";
  const workMode = bookmark.work_mode || "";

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-blue-200 transition-all duration-300 overflow-hidden flex flex-col">
      {/* Blue gradient top bar */}
      <div className="h-1.5 bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400" />

      <div className="p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            {bookmark.company_logo_url ? (
              <img
                src={bookmark.company_logo_url}
                alt={company}
                className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-100 p-1 shrink-0"
              />
            ) : (
              <div className="w-11 h-11 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-blue-600" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 leading-tight text-base">
                {title}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5 font-medium truncate">{company}</p>
            </div>
          </div>

          {/* Remove bookmark */}
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            disabled={isRemoving}
            title="Remove bookmark"
            className="p-1.5 rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition shrink-0"
          >
            {isRemoving ? (
              <Loader2 size={15} className="animate-spin text-red-400" />
            ) : (
              <BookmarkX size={15} />
            )}
          </button>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-4 flex-1">
          {workMode && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full border ${
              workMode.toLowerCase() === "remote"
                ? "bg-blue-50 text-blue-700 border-blue-100"
                : "bg-gray-100 text-gray-600 border-gray-200"
            }`}>
              <Wifi size={10} /> {workMode}
            </span>
          )}
          {bookmark.stipend && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full border border-emerald-100">
              <IndianRupee size={10} /> {bookmark.stipend.toLocaleString()}/mo
            </span>
          )}
        </div>

        {/* Saved at */}
        <p className="text-xs text-gray-400 mb-4">
          Saved on {new Date(bookmark.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
        </p>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t border-gray-100 mt-auto">
          <button
            onClick={onView}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold hover:from-blue-700 hover:to-indigo-700 transition shadow-sm shadow-blue-100"
          >
            View Role <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
